/*
 * Presentation Templates — fetch lifecycle + two-level browse state (grid of
 * presentations vs. one presentation's slide grid) + flat selection state.
 *
 * Mirrors the source/commitSelection parameterisation pattern used elsewhere
 * in Deckorate for the same reason: the panel is mounted both in-pane
 * (fetches/inserts directly) and inside the Office Dialog window (mirrors a
 * load state the pane owns, commits via a messageParent round-trip) — see
 * asset-library/hooks/useDialogPresentationTemplates.ts. This hook and its
 * services are entirely independent of the slide-templates feature — no
 * imports from it.
 *
 * Selection stays flat across every open presentation's slides: a slide's
 * `id` is already globally unique per the manifest, so there's no need to
 * scope selection to `openPresentationId`.
 *
 * Every presentation's REAL, full slide list already arrives from
 * fetchPresentationTemplates itself (it eagerly resolves each presentation's
 * per-file `.cache` sibling in parallel — see
 * services/presentationTemplatesApi.ts's `resolveFullSlideLists`), so opening
 * a presentation here is just a lookup, not a fetch.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import { getErrorMessage, isAbortError } from "../../shared/services/connectorErrors";
import type { ExtractedSlideImage } from "../../shared/services/pptxSlideImageExtraction";
import {
  fetchPresentationTemplates,
  logPresentationTemplatesError,
  type PresentationTemplate,
  type PresentationTemplateSlide,
} from "../services/presentationTemplatesApi";
import { insertPresentationTemplates } from "../services/presentationTemplateInsert";

const FETCH_FAILED_MESSAGE = "Couldn't load presentation templates";
const INSERT_FAILED_MESSAGE = "Inserting the selected slide(s) failed.";
const NO_SLIDE_DATA_MESSAGE = "These slides have no insertable content yet.";

export type PresentationTemplatesLoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; presentations: readonly PresentationTemplate[] };

export type PresentationTemplatesSource =
  /** Fetch here. Only valid where the request can be authorised — the pane. */
  | { readonly kind: "api" }
  /** Mirror a load state owned elsewhere, and delegate Retry to its owner. */
  | { readonly kind: "external"; readonly state: PresentationTemplatesLoadState; readonly onRetry: () => void };

export interface UsePresentationTemplatesOptions {
  /** Defaults to `{kind:"api"}` — the in-pane path. */
  readonly source?: PresentationTemplatesSource;
  /**
   * Overrides how a selection is committed, resolving with the number of
   * slides that landed in the deck. Defaults to inserting them here. Pass a
   * stable (memoised) callback.
   */
  readonly commitSelection?: (selectedIds: readonly string[]) => Promise<number>;
  /** Overrides how a slide card's real photo(s) are loaded. */
  readonly loadThumbnail?: (slide: PresentationTemplateSlide) => Promise<readonly ExtractedSlideImage[]>;
}

const API_SOURCE: PresentationTemplatesSource = { kind: "api" };

export function usePresentationTemplates({
  source = API_SOURCE,
  commitSelection,
}: UsePresentationTemplatesOptions = {}) {
  const http = useDeckorateHttp();
  const [fetchedState, setFetchedState] = useState<PresentationTemplatesLoadState>({ status: "loading" });
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [openPresentationId, setOpenPresentationId] = useState<string | null>(null);
  const [insertError, setInsertError] = useState<string | null>(null);
  const [insertStatus, setInsertStatus] = useState<string | null>(null);
  const [isInserting, setIsInserting] = useState(false);

  const isMountedRef = useRef(true);
  const isExternal = source.kind === "external";

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setFetchedState({ status: "loading" });
      try {
        const presentations = await fetchPresentationTemplates(http, signal ? { signal } : {});
        if (!isMountedRef.current) return;
        setFetchedState({ status: "ready", presentations });
      } catch (error) {
        if (isAbortError(error)) return;
        const message = getErrorMessage(error, FETCH_FAILED_MESSAGE);
        logPresentationTemplatesError("presentation-templates-fetch", message);
        if (!isMountedRef.current) return;
        setFetchedState({ status: "error", message });
      }
    },
    [http],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // An external source owns its own loading; fetching here would duplicate a
    // request this context can't even authorise.
    if (isExternal) return;
    const controller = new AbortController();
    load(controller.signal).catch((error: unknown) =>
      logPresentationTemplatesError("presentation-templates-fetch-unhandled", getErrorMessage(error, FETCH_FAILED_MESSAGE)),
    );
    return () => controller.abort();
  }, [isExternal, load]);

  const externalRetry = source.kind === "external" ? source.onRetry : null;
  const retry = useCallback(() => {
    if (externalRetry) {
      externalRetry();
      return;
    }
    load().catch((error: unknown) =>
      logPresentationTemplatesError("presentation-templates-retry", getErrorMessage(error, FETCH_FAILED_MESSAGE)),
    );
  }, [externalRetry, load]);

  const loadState = source.kind === "external" ? source.state : fetchedState;
  const presentations = loadState.status === "ready" ? loadState.presentations : [];

  const openPresentation = useCallback((id: string) => setOpenPresentationId(id), []);
  const closePresentation = useCallback(() => setOpenPresentationId(null), []);

  // A manifest refresh can drop the presentation currently open (or return a
  // different set entirely) — fall back to the grid rather than rendering a
  // preview for a presentation that no longer exists.
  useEffect(() => {
    if (openPresentationId === null) return;
    if (presentations.some((presentation) => presentation.id === openPresentationId)) return;
    setOpenPresentationId(null);
  }, [presentations, openPresentationId]);

  const openPresentationEntry = useMemo(
    () => presentations.find((presentation) => presentation.id === openPresentationId) ?? null,
    [presentations, openPresentationId],
  );

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allSlides = useMemo(() => presentations.flatMap((presentation) => presentation.slides), [presentations]);

  // Only slides still present in the current manifest count as selected — a
  // retry that returns a different set must not leave phantom ids selected.
  const selectedSlides = useMemo(
    () => allSlides.filter((slide) => selectedIds.has(slide.id)),
    [allSlides, selectedIds],
  );
  const insertableSelected = useMemo(() => selectedSlides.filter((slide) => slide.insertable), [selectedSlides]);

  /** Shared by `insertSelected` and `insertAllSlidesOf` — both are just "commit
   *  this specific id list", differing only in where the ids come from
   *  (the flat selection vs. one presentation's entire slide set). Returns
   *  whether the commit actually succeeded, so a failed insert can leave the
   *  caller's own state (e.g. the flat selection) untouched for the user to
   *  retry, matching Slide Templates' own insertSelected. */
  const commitIds = useCallback(
    async (ids: readonly string[]): Promise<boolean> => {
      if (isInserting || ids.length === 0) return false;
      setIsInserting(true);
      setInsertError(null);
      setInsertStatus(null);
      try {
        const inserted = commitSelection ? await commitSelection(ids) : await insertPresentationTemplates(allSlides, ids);
        if (isMountedRef.current) {
          const slideCountLabel = inserted === 1 ? "1 slide" : `${inserted} slides`;
          setInsertStatus(`Inserted ${slideCountLabel}.`);
        }
        return true;
      } catch (error) {
        const message = getErrorMessage(error, INSERT_FAILED_MESSAGE);
        logPresentationTemplatesError("presentation-templates-insert", message);
        if (isMountedRef.current) setInsertError(message);
        return false;
      } finally {
        if (isMountedRef.current) setIsInserting(false);
      }
    },
    [isInserting, allSlides, commitSelection],
  );

  const insertSelected = useCallback(async () => {
    if (isInserting || selectedSlides.length === 0) return;
    if (insertableSelected.length === 0) {
      setInsertError(NO_SLIDE_DATA_MESSAGE);
      return;
    }
    const succeeded = await commitIds(insertableSelected.map((slide) => slide.id));
    if (succeeded && isMountedRef.current) setSelectedIds(new Set());
  }, [isInserting, selectedSlides, insertableSelected, commitIds]);

  /** Inserts every insertable slide of `presentationId` directly — the grid
   *  card's "insert all slides" action, independent of the flat selection
   *  (doesn't touch `selectedIds`, and works without ever opening the
   *  presentation's own slide grid first). */
  const insertAllSlidesOf = useCallback(
    async (presentationId: string) => {
      if (isInserting) return;
      const presentation = presentations.find((candidate) => candidate.id === presentationId);
      const ids = presentation?.slides.filter((slide) => slide.insertable).map((slide) => slide.id) ?? [];
      if (ids.length === 0) {
        setInsertError(NO_SLIDE_DATA_MESSAGE);
        return;
      }
      await commitIds(ids);
    },
    [isInserting, presentations, commitIds],
  );

  return {
    loadState,
    insertStatus,
    presentations,
    openPresentationId,
    /** The currently-open presentation's own record, or null in grid view. */
    activePresentation: openPresentationEntry,
    openPresentation,
    closePresentation,
    selectedIds,
    selectedCount: selectedSlides.length,
    toggleSelected,
    retry,
    insertSelected,
    insertAllSlidesOf,
    isInserting,
    insertError,
  };
}
